// R-131-FIN-DECISION-F — stacked pathway cashflow chart with overlay toggle.

import React, { useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { SafeResponsiveContainer } from '../../ui/SafeResponsiveContainer';
import { buildPathwayCashflows } from '../../../lib/finDecision/pathwayCashflows';
import type { Pathway } from '../../../lib/finDecision/types';

export interface PathwayCashflowChartProps {
  pathways: Pathway[];
  now?: Date;
}

type ViewMode = 'stacked' | 'overlay';

interface ChartRow {
  year: number;
  [key: string]: number;
}

// Aurora cashflow palette. Cost series map onto the danger/warning/chart tokens
// (a coherent "outflow" gradient) and revenue onto success; all are theme
// tokens that flip dark/light so the stack reads on both the dark app and the
// light theme. NOT data — purely the series colour key.
const STACK_COLOR_VARS = {
  capex: 'var(--danger)',
  opex: 'var(--conditional-high)',
  fuel: 'var(--warning)',
  refurb: 'var(--chart-3)',
  revenue: 'var(--success)',
} as const;

// Per-pathway categorical palette for the overlay view — the shared chart
// tokens (var(--chart-1..4)) keep it consistent with the risk-matrix/band dots.
const OVERLAY_COLOR_VARS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
] as const;

function shortLabel(pathway: Pathway): string {
  return pathway.name.length > 18 ? `${pathway.name.slice(0, 16)}…` : pathway.name;
}

function formatUsdMillions(value: number): string {
  return `$${(value / 1_000_000).toFixed(1)}M`;
}

interface TooltipEntry {
  name?: string | number;
  value?: number | string;
  color?: string;
  dataKey?: string | number;
}

/**
 * Custom tooltip content — matches the PathwayBandChart card design (clean
 * surface-1 card, muted label / primary value rows) instead of the default
 * Recharts box where every row's text inherits the series colour. Each cashflow
 * series keeps its colour identity via a small swatch (so rows map back to the
 * stacked bars) while the value column stays high-contrast on dark and light.
 * The Net CFO summary row is divided off and emphasised.
 */
function CashflowTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: number | string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const rows = payload.filter((e) => e.dataKey !== 'net');
  const net = payload.find((e) => e.dataKey === 'net');
  return (
    <div
      className="rounded-md border border-border bg-surface-1 px-3 py-2 shadow-lg"
      data-testid="pathway-cashflow-tooltip"
    >
      <div className="type-meta font-semibold text-text-primary">{label}</div>
      <dl className="mt-1.5 space-y-0.5 type-meta">
        {rows.map((entry) => (
          <div
            key={String(entry.dataKey ?? entry.name)}
            className="flex items-center justify-between gap-4"
          >
            <dt className="flex items-center gap-1.5 text-text-secondary">
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
                aria-hidden="true"
              />
              {String(entry.name ?? '')}
            </dt>
            <dd className="tabular-nums text-text-primary">
              {formatUsdMillions(Number(entry.value))}
            </dd>
          </div>
        ))}
        {net ? (
          <div className="mt-1 flex items-center justify-between gap-4 border-t border-border pt-1 font-semibold">
            <dt className="text-text-secondary">{String(net.name ?? 'Net CFO')}</dt>
            <dd className="tabular-nums text-text-primary">
              {formatUsdMillions(Number(net.value))}
            </dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

interface LegendPayloadEntry {
  value?: string | number;
  color?: string;
  dataKey?: string | number;
  type?: string;
}

/**
 * Custom legend content — Recharts has no rounded-square iconType, so this
 * renders each payload entry with a `rounded-[2px]` swatch (inline
 * backgroundColor) that mirrors the CashflowTooltip swatch, laid out as a
 * centered flex-wrap row. The Net CFO line entry uses the same swatch so it
 * stays readable on dark and light themes.
 */
function CashflowLegend({ payload }: { payload?: LegendPayloadEntry[] }) {
  if (!payload || payload.length === 0) return null;
  return (
    <ul className="flex flex-wrap items-start justify-center gap-x-4 gap-y-1 type-meta">
      {payload.map((entry) => (
        <li
          key={String(entry.dataKey ?? entry.value)}
          // Full label, no clipping: the swatch stays pinned to the first line
          // (mt-[3px] aligns it with the cap height) while a long pathway name
          // such as "Phase 2 Reciprocating Engine 2029" wraps onto further
          // lines instead of truncating to "…". max-w caps each item so one
          // long name wraps rather than stretching the legend row.
          className="flex max-w-[16rem] items-start gap-1.5 text-text-secondary"
        >
          <span
            className="mt-[3px] inline-block h-2 w-2 shrink-0 rounded-[2px]"
            style={{ backgroundColor: entry.color }}
            aria-hidden="true"
          />
          <span className="whitespace-normal break-words">{String(entry.value ?? '')}</span>
        </li>
      ))}
    </ul>
  );
}

export function PathwayCashflowChart({ pathways, now }: PathwayCashflowChartProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('stacked');
  const [activePathwayId, setActivePathwayId] = useState<string>(
    () => pathways[0]?.id ?? '',
  );

  const anchor = now ?? new Date();

  const stackedData = useMemo(() => {
    const active = pathways.find((p) => p.id === activePathwayId) ?? pathways[0];
    if (!active) return [];
    const flows = buildPathwayCashflows(active, anchor);
    return flows.map((row) => ({
      year: row.year,
      capex: -row.capex,
      opex: -row.opex,
      fuel: -row.fuel,
      refurb: -row.refurbishment,
      revenue: row.revenue,
      net: row.netCashFlow,
    }));
  }, [activePathwayId, anchor, pathways]);

  const overlayData = useMemo(() => {
    if (pathways.length === 0) return [] as ChartRow[];
    const byYear = new Map<number, ChartRow>();
    pathways.forEach((pathway) => {
      const flows = buildPathwayCashflows(pathway, anchor);
      flows.forEach((row) => {
        const existing = byYear.get(row.year) ?? { year: row.year };
        existing[`net_${pathway.id}`] = row.netCashFlow;
        byYear.set(row.year, existing);
      });
    });
    return [...byYear.values()].sort((a, b) => a.year - b.year);
  }, [anchor, pathways]);

  if (pathways.length === 0) {
    return null;
  }

  return (
    <div
      className="rounded-lg border border-border bg-surface-1 p-4"
      data-testid="pathway-cashflow-chart"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary">Pathway cashflows</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={`rounded-md px-2.5 py-1 text-xs border ${
              viewMode === 'stacked'
                ? 'border-primary text-primary bg-primary/10'
                : 'border-border text-text-secondary'
            }`}
            onClick={() => setViewMode('stacked')}
          >
            Stacked CFO
          </button>
          <button
            type="button"
            className={`rounded-md px-2.5 py-1 text-xs border ${
              viewMode === 'overlay'
                ? 'border-primary text-primary bg-primary/10'
                : 'border-border text-text-secondary'
            }`}
            onClick={() => setViewMode('overlay')}
          >
            Overlay net CFO
          </button>
        </div>
      </div>

      {viewMode === 'stacked' ? (
        <>
          {pathways.length > 1 ? (
            <div className="mb-2 flex flex-wrap gap-2">
              {pathways.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`rounded-md px-2 py-0.5 text-xs border ${
                    activePathwayId === p.id
                      ? 'border-primary text-primary'
                      : 'border-border text-text-secondary'
                  }`}
                  onClick={() => setActivePathwayId(p.id)}
                >
                  {shortLabel(p)}
                </button>
              ))}
            </div>
          ) : null}
          <div className="h-72">
            <SafeResponsiveContainer width="100%" height="100%">
              <ComposedChart data={stackedData} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
                <XAxis dataKey="year" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} stroke="var(--border)" />
                <YAxis tickFormatter={formatUsdMillions} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} stroke="var(--border)" />
                <Tooltip content={<CashflowTooltip />} cursor={{ fill: 'var(--state-hover)' }} />
                <Legend content={<CashflowLegend />} />
                <Bar isAnimationActive={false} dataKey="capex" stackId="costs" fill={STACK_COLOR_VARS.capex} name="CAPEX" radius={[2, 2, 0, 0]} />
                <Bar isAnimationActive={false} dataKey="opex" stackId="costs" fill={STACK_COLOR_VARS.opex} name="OPEX" radius={[2, 2, 0, 0]} />
                <Bar isAnimationActive={false} dataKey="fuel" stackId="costs" fill={STACK_COLOR_VARS.fuel} name="Fuel" radius={[2, 2, 0, 0]} />
                <Bar isAnimationActive={false} dataKey="refurb" stackId="costs" fill={STACK_COLOR_VARS.refurb} name="Refurb" radius={[2, 2, 0, 0]} />
                <Bar isAnimationActive={false} dataKey="revenue" fill={STACK_COLOR_VARS.revenue} name="Revenue" radius={[2, 2, 0, 0]} />
                <Line isAnimationActive={false} type="monotone" dataKey="net" stroke="var(--text-primary)" strokeWidth={2} dot={false} name="Net CFO" />
              </ComposedChart>
            </SafeResponsiveContainer>
          </div>
        </>
      ) : (
        <div className="h-72">
          <SafeResponsiveContainer width="100%" height="100%">
            <ComposedChart data={overlayData} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} stroke="var(--border)" />
              <YAxis tickFormatter={formatUsdMillions} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} stroke="var(--border)" />
              <Tooltip content={<CashflowTooltip />} cursor={{ fill: 'var(--state-hover)' }} />
              <Legend content={<CashflowLegend />} />
              {pathways.map((pathway, index) => (
                <Line isAnimationActive={false}
                  key={pathway.id}
                  type="monotone"
                  dataKey={`net_${pathway.id}`}
                  stroke={OVERLAY_COLOR_VARS[index % OVERLAY_COLOR_VARS.length]}
                  strokeWidth={2}
                  dot={false}
                  // Full pathway name into the legend (no slice/"…"). The custom
                  // CashflowLegend wraps long names onto multiple lines.
                  name={pathway.name}
                />
              ))}
            </ComposedChart>
          </SafeResponsiveContainer>
        </div>
      )}
    </div>
  );
}
